/**
 * Manager Feedback API Models
 * Type definitions for manager feedback dashboard API requests and responses
 */

// =============================================================================
// Common Types
// =============================================================================

/**
 * Feedback status values
 */
export type FeedbackStatus = 'new' | 'acknowledged' | 'in_progress' | 'resolved' | 'wont_fix';

/**
 * Feedback type values
 */
export type FeedbackType = 'bug_report' | 'feature_request' | 'general' | 'compliment';

/**
 * Status labels for UI display
 */
export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: 'New',
  acknowledged: 'Acknowledged',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  wont_fix: "Won't Fix"
};

/**
 * Type labels for UI display
 */
export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  bug_report: 'Bug Report',
  feature_request: 'Feature Request',
  general: 'General',
  compliment: 'Compliment'
};

// =============================================================================
// Pagination
// =============================================================================

/**
 * Generic paginated response wrapper
 */
export interface PaginatedResponse<T> {
  total: number;
  page: number;
  size: number;
  pages: number;
  items: T[];
}

// =============================================================================
// Feedback List
// =============================================================================

/**
 * Feedback item in the list view
 */
export interface FeedbackDashboardItemSchema {
  id: string;
  rating: number | null;
  feedback_text: string | null;
  feedback_type: FeedbackType | null;
  status: FeedbackStatus;
  created_at: string;
  user_name: string | null;
  user_email: string | null;
  notes_count: number;
}

/**
 * Parameters for listing feedback
 */
export interface FeedbackListParams {
  page?: number;
  size?: number;
  status?: FeedbackStatus;
  feedback_type?: FeedbackType;
  rating?: number;
  order_by?: string;
}

// =============================================================================
// Feedback Detail
// =============================================================================

/**
 * Feedback note response
 */
export interface FeedbackNoteSchema {
  id: string;
  note_text: string;
  author_name: string;
  author_email: string;
  created_at: string;
}

/**
 * Detailed feedback response with notes
 */
export interface FeedbackDetailSchema {
  id: string;
  rating: number | null;
  feedback_text: string | null;
  feedback_type: FeedbackType | null;
  status: FeedbackStatus;
  created_at: string;
  updated_at: string;
  chat_session_id: string | null;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  notes_count: number;
  notes: FeedbackNoteSchema[];
}

// =============================================================================
// Request Bodies
// =============================================================================

/**
 * Request body for adding a note
 */
export interface AddNoteRequest {
  note_text: string; // 1-2000 chars
}

/**
 * Request body for updating status
 */
export interface UpdateStatusRequest {
  status: FeedbackStatus;
}

/**
 * Request body for sending email
 */
export interface SendEmailRequest {
  subject: string; // 1-200 chars
  body: string;    // 1-5000 chars
}

// =============================================================================
// Response Types
// =============================================================================

/**
 * Response from send email endpoint
 */
export interface SendEmailResponse {
  message: string;
  recipient_email: string;
}
